import { LinkId } from '../../../../../common/AnalyticsContext/LinkId';
import { useSearchFilters } from '../../../../../common/components/SearchHeader/filters/useSearchFilters';
import { COVID_19_FLU_VACCINATIONS } from '../../../../../common/components/SearchHeader/types/FilterChoice';
import { useHannibalQuery } from '../../../../../common/schema/hannibal/HannibalContext';
import { MoreFiltersOptionsDocument } from '../../../../../common/schema/hannibal/types.generated';
import { useMobile } from '../../../../../common/services/screenSize/ScreenSizeService';
import { useVariables } from '../../../../useSearchQuery/useVariables';
import { DrawerEnumFilter } from '../../../EnumFilter/DrawerEnumFilter/DrawerEnumFilter';
import { useOverlay } from '../../../overlay/OverlayContext';
import { EXCLUDE_OPTIONS } from '../../PaymentMethodsFilter';
import { useAppliedFilterCount, useClearFilters, useOptions } from '../MoreFiltersHelpers';
import {
  DrawerContainer,
  FilterContainer,
  Link,
  Spacer,
  StyledFilterSummary,
  Title,
  TitleBarContainer,
} from './MoreFiltersDrawer.styles';

export const MoreFiltersDrawer = () => {
  const [filters, setFilters] = useSearchFilters();
  const { clear } = useOverlay();

  const { topic } = filters;

  const { data } = useHannibalQuery(MoreFiltersOptionsDocument, {
    variables: useVariables(filters),
  });

  const onResetClick = useClearFilters();

  const languageOptions = useOptions(data?.searchProfiles.facetsWithCounts.languagesName.values);

  const paymentOptions = useOptions(
    data?.searchProfiles.facetsWithCounts.paymentMethods.values,
    EXCLUDE_OPTIONS,
  );

  const schemeOptions = useOptions(data?.searchProfiles.facetsWithCounts.governmentSchemes.values);

  const handleOptionChange = (filterKey: string, filterValue?: string) =>
    setFilters({ ...filters, [filterKey]: filterValue });

  const handleOptionClear = (filterKey: string) =>
    setFilters({ ...filters, [filterKey]: undefined });

  const appliedFilterCount = useAppliedFilterCount();

  const isCovax = topic?.specialist === COVID_19_FLU_VACCINATIONS;
  const shouldShowPaymentOptions = paymentOptions.length > 0 && !isCovax;
  const shouldShowSchemeOptions = schemeOptions.length > 0 && !isCovax;

  const isMobile = useMobile();

  return (
    <DrawerContainer>
      <TitleBarContainer>
        <Link onClick={clear}>Close</Link>
        <Title>More Filters</Title>
        <Link onClick={onResetClick}>Reset</Link>
      </TitleBarContainer>
      <FilterContainer>
        {appliedFilterCount > 0 && (
          <>
            <StyledFilterSummary handleOptionClear={handleOptionClear} />
            <Spacer />
          </>
        )}
        {languageOptions.length > 0 && (
          <>
            <DrawerEnumFilter<string | undefined>
              row={!isMobile}
              value={filters.language}
              filterTitle="Languages spoken"
              options={languageOptions}
              onChange={value => handleOptionChange('language', value)}
              trackingId={LinkId.FILTER_OPTION_LANGUAGES_SPOKEN}
            />
            <Spacer />
          </>
        )}
        {shouldShowPaymentOptions && (
          <>
            <DrawerEnumFilter<string | undefined>
              row={!isMobile}
              value={filters.paymentMethod}
              filterTitle="Payment methods accepted"
              options={paymentOptions}
              onChange={value => handleOptionChange('paymentMethod', value)}
              trackingId={LinkId.FILTER_OPTION_PAYMENT_METHOD}
            />
            <Spacer />
          </>
        )}
        {shouldShowSchemeOptions && (
          <>
            <DrawerEnumFilter<string | undefined>
              row={!isMobile}
              value={filters.governmentScheme}
              filterTitle="Government schemes"
              options={schemeOptions}
              onChange={value => handleOptionChange('governmentScheme', value)}
              trackingId={LinkId.FILTER_OPTION_GOVT_SCHEMES}
            />
            <Spacer />
          </>
        )}
      </FilterContainer>
    </DrawerContainer>
  );
};
